import React, { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import AdminLayout from './components/AdminLayout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import HeroEditorPage from './pages/HeroEditorPage';
import AboutEditorPage from './pages/AboutEditorPage';
import ProductsEditorPage from './pages/ProductsEditorPage';
import ContactEditorPage from './pages/ContactEditorPage';
import FooterEditorPage from './pages/FooterEditorPage';
import NewsEditorPage from './pages/NewsEditorPage';
import MessagesPage from './pages/MessagesPage';
import MediaPage from './pages/MediaPage';
import SettingsPage from './pages/SettingsPage';
import UsersPage from './pages/UsersPage';
import { useAdminAuth } from './hooks/useAdminAuth';
import adminApi from './api/adminApi';

const ProtectedRoute = ({ children, superadminOnly }) => {
  const { isLoggedIn, getUser } = useAdminAuth();
  if (!isLoggedIn()) return <Navigate to="/admin/login" replace />;
  if (superadminOnly && getUser()?.role !== 'superadmin') return <Navigate to="/admin/dashboard" replace />;
  return children;
};

const AdminShell = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const { getUser } = useAdminAuth();
  const user = getUser();

  useEffect(() => {
    const fetchUnread = () => {
      adminApi.get('/admin/messages', { params: { page: 1, limit: 1, unread_only: true } })
        .then(r => setUnreadCount(r.data?.unread_count ?? 0))
        .catch(() => {});
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 60000);
    return () => clearInterval(interval);
  }, []);

  return (
    <AdminLayout unreadCount={unreadCount} user={user}>
      <Routes>
        <Route index element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="dashboard" element={<DashboardPage />} />
        <Route path="hero" element={<HeroEditorPage />} />
        <Route path="about" element={<AboutEditorPage />} />
        <Route path="products" element={<ProductsEditorPage />} />
        <Route path="contact" element={<ContactEditorPage />} />
        <Route path="footer" element={<FooterEditorPage />} />
        <Route path="news" element={<NewsEditorPage />} />
        <Route path="messages" element={<MessagesPage />} />
        <Route path="media" element={<MediaPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="users" element={
          <ProtectedRoute superadminOnly>
            <UsersPage />
          </ProtectedRoute>
        } />
        <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
      </Routes>
    </AdminLayout>
  );
};

const AdminApp = () => {
  const { isLoggedIn } = useAdminAuth();

  return (
    <>
      <Toaster position="top-right" theme="dark" richColors />
      <Routes>
        <Route path="login" element={
          isLoggedIn() ? <Navigate to="/admin/dashboard" replace /> : <LoginPage />
        } />
        <Route path="*" element={
          <ProtectedRoute>
            <AdminShell />
          </ProtectedRoute>
        } />
      </Routes>
    </>
  );
};

export default AdminApp;
